import { useEffect, useState } from 'react'


import _ from 'lodash'
import { useForm, FormProvider } from 'react-hook-form'


import { env } from '@/env'
import { DigitalProductCheckoutAditionalInformationFields } from '@/features/user/additional-information'
import { getAddInfosByPriceId } from '@/features/user/additional-information/utils'
import { withResourceRecord } from '@/shared/contexts'
import { useResourceRecordContext, useModalContext, useSession } from '@/shared/hooks'
import { useAppRouter as useRouter } from '@/shared/hooks/useAppRouter'
import { useDigitalProduct } from '@/shared/hooks/useDigitalProduct'
import { api } from '@/shared/lib/api'
import { useAuthActions } from '@/shared/lib/auth/actions'
import { showErrorAlert } from '@/shared/lib/errors'
import { authPaths } from '@/shared/lib/routing/auth'
import { truncateName } from '@/shared/lib/string/truncate-name'
import { Button, Modal } from '@/shared/ui'
import { showRedirectCountdownDialog } from '@/shared/ui/dialog'
import { AppIcon  } from '@/shared/ui/icons'
import { AppMenuScrollArea } from '@/shared/ui/Layout/menu/AppMenuScrollArea'



import { getConfig } from './config'

const isDev = env.NODE_ENV == 'development'

export const DigitalProductInvitesAssign = withResourceRecord(({ id }) => {
  const { record: invite } = useResourceRecordContext()
  const { showModal, hideModal } = useModalContext()
  const { signOut } = useAuthActions()
  const router = useRouter()
  const digitalProductAttrs = useDigitalProduct()
  const { digitalProduct, digitalProductId: eventSlug } = digitalProductAttrs

  const handleShowInviteModal = () => {
    showModal({
      component: DigitalProductInvitesAssignModal,
      invite,
      ...digitalProductAttrs,
      onShowSignIn: handleShowAuthPage,
      onShowSignUp: () => handleShowAuthPage({ action: 'sign-up' })
    })
  }
  const handleShowAuthPage = ({ action = 'sign-in' } = {}) => {
    const target =
      action === 'sign-up'
        ? authPaths.signUp({ callbackUrl: window.location.href })
        : authPaths.signIn({ callbackUrl: window.location.href })

    hideModal()
    router.push(target)
  }

  useEffect(() => {
    if (invite) handleShowInviteModal()
  }, [invite])

  return <></>
}, getConfig)

const DigitalProductInvitesAssignModal = ({
  invite,
  digitalProduct,
  basePathWithId,
  onShowSignIn,
  onShowSignUp
}) => {
  const methods = useForm()
  const { data: session, status: sessionStatus } = useSession()
  const authenticated =
    sessionStatus === 'success' && !!session && !!session?.user
  const user = session?.user
  const router = useRouter()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const kind =
    invite?.price_id && invite?.guild_id
      ? 'all'
      : invite?.price_id
      ? 'price'
      : 'guild'
  // Reusa `getAddInfosByPriceId` em vez de refazer o filtro: a cópia local não
  // tinha o `sortBy('position')`, então os campos saíam por `id` e a ordem
  // divergia do modal bloqueante.
  const invitePriceId = _.get(invite, 'price_id')
  const addInfos = invitePriceId
    ? getAddInfosByPriceId({ digitalProduct, priceId: invitePriceId })
    : []

  const handleRedirectToGamification = async (alreadyAcceptedInvite = false) => {
    await showRedirectCountdownDialog({
      title: alreadyAcceptedInvite ? 'Bem-vindo de volta!' : 'Convite aceito!',
      seconds: 5,
      confirmLabel: 'Ir para área gamificada'
    })
    router.push(basePathWithId)
  }
  const handleLoadProgress = async () => {
    setIsLoading(true)

    try {
      // _skipAccessRecovery: this call only checks whether *this* invite was
      // already explicitly accepted. A 401/403 here must not be turned into a
      // silent default-entry grant — that would masquerade as "already
      // accepted" and skip the required-form step below (see bug
      // invite-link-auto-entry-skips-required-form).
      const resProgress = await api.get(
        `/user/digital_products/${eventSlug}/gamification/progress`,
        { _skipAccessRecovery: true }
      )
      const acceptedInvites = _.get(
        resProgress,
        'data.record.accepted_invites',
        []
      )

      if (
        acceptedInvites &&
        acceptedInvites.length &&
        acceptedInvites.includes(invite.id)
      )
        handleRedirectToGamification(true)
      else setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
    }
  }
  const handleSignOut = async () => {
    await signOut()
  }
  const handleAcceptInvite = async data => {
    if (isSubmitting) return

    setIsSubmitting(true)

    try {
      await api.post(
        `/user/gamification_invites/${invite.code}/accept_invitation`
      )

      if (invite?.price_id && addInfos && addInfos.length) {
        try {
          const resProgress = await api.get(
            `/user/digital_products/${eventSlug}/gamification/progress`
          )
          const progress = _.get(resProgress, 'data.record', {})

          const ticket = _.get(progress, 'user_tickets', []).find(
            userTicket => userTicket?.price_id == invite?.price_id
          )

          if (ticket) {
            await api.put(`/user/tickets/${ticket?.id}`, {
              record: {
                additional_informations: data?.additional_informations
              }
            })
          }
        } catch (error) {}
      }

      handleRedirectToGamification()
    } catch (error) {
      showErrorAlert(error)
    }

    setIsSubmitting(false)
  }

  useEffect(() => {
    if (authenticated) handleLoadProgress()
  }, [authenticated])

  return (
    <Modal
      centered
      title="Confirmar seu convite"
      show
      backdrop="static"
      keyboard={false}
    >
      {/* O `<form>` envolve corpo e rodapé, e não o contrário: é o que permite
          o CTA morar no `Modal.Footer`, fixo, enquanto só os campos rolam. Sem
          `min-h-0` o form não encolhe abaixo do conteúdo e o corpo para de
          rolar. */}
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleAcceptInvite)}
          className="flex min-h-0 flex-1 flex-col w-full"
        >
      {/* Mesma área rolável do `Survey.jsx`: os dois modais de informações
          adicionais precisam rolar igual. */}
      <AppMenuScrollArea
        label="Dados do convite"
        hintLabel="Role para ver mais campos do convite"
        className={isLoading ? 'relative' : undefined}
        // Sem esticar: conteúdo curto deixaria um vão antes do botão.
        grow={false}
      >
        {isLoading && (
          <div className="absolute inset-0 z-[3] flex items-center justify-center bg-white/75">
            <div className="w-8 h-8 border-2 border-brand/30 border-t-brand rounded-full animate-spin" role="status">
              <span className="sr-only">Carregando...</span>
            </div>
          </div>
        )}
        {/* `pb-0`: quem fecha o espaçamento inferior é o bloco seguinte ou o
            rodapé — somar o padding daqui vira vão branco. */}
        <div className="flex flex-col items-center justify-center gap-6 px-10 pt-10 pb-0 lg:px-[3.75rem] lg:pt-12">
          <div className="mb-2">
            {authenticated && _.get(user, 'avatar.url') ? (
              <div className="mx-auto aspect-square w-[40px] h-[40px] lg:w-[60px] lg:h-[60px]">
                <img
                  src={_.get(user, 'avatar.url')}
                  alt=""
                  className="rounded-full object-cover object-center"
                />
              </div>
            ) : (
              <AppIcon  name="party-horn" className="w-[40px] h-[40px] lg:w-[60px] lg:h-[60px] text-brand flex items-center justify-center" />
            )}
          </div>
          <span className="text-center text-gray-800 font-semibold block text-base max-w-[300px]">
            {authenticated ? (
              <>
                <span className="font-bold">{truncateName(user?.name, 20)}</span>,
                você
              </>
            ) : (
              'Você'
            )}{' '}
            foi convidado para participar da{' '}
            {['all', 'guild'].includes(kind) && (
              <span className="font-bold">
                Guild{' '}
                <span className="px-1 text-white font-bold bg-primary">
                  {_.get(invite, 'guild.name')}
                </span>{' '}
                na
              </span>
            )}{' '}
            gamificação do
          </span>
          <div className="flex items-center gap-4 mb-4">
            <img
              id="logo"
              alt="Logo"
              src={_.get(digitalProduct, 'logo.file.url')}
              className="h-[40px] lg:h-[40px] xl:h-[50px] max-w-full object-contain object-center rounded"
            />
            <span className="font-bold text-brand text-xl block leading-none max-w-[250px]">
              {digitalProduct?.name}
            </span>
          </div>
          {!isLoading && (
            <>
              {authenticated ? (
                // O `mb-6` do `Field` separa um campo do seguinte, mas no
                // último vira só sobra antes do que vem depois.
                <div className="w-full [&>*:last-child]:mb-0">
                  {addInfos &&
                    addInfos.map((addInfo, addInfoIndex) => (
                      <DigitalProductCheckoutAditionalInformationFields
                        key={addInfo?.id}
                        namePrefix={`additional_informations.${addInfoIndex}`}
                        {...addInfo}
                      />
                    ))}
                </div>
              ) : (
                <div className="flex gap-4">
                  <Button type="button" variant="ghost" onClick={onShowSignIn}>
                    Entrar
                  </Button>

                  <Button type="button" variant="primary" onClick={onShowSignUp}>
                    Criar uma conta
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
        {authenticated && !isLoading && (
          <div className="w-full px-10 pb-4 pt-3 lg:px-[3.75rem]">
            <span className="text-gray-500 text-xs block -mb-2">
              Não é você?{' '}
              <span
                className="text-gray-600 cursor-pointer font-semibold hover:text-info"
                onClick={handleSignOut}
              >
                Clique aqui
              </span>{' '}
              para sair
            </span>
          </div>
        )}
      </AppMenuScrollArea>
          {authenticated && !isLoading && (
            <Modal.Footer>
              <Button
                type="submit"
                variant="success"
                disabled={isSubmitting}
                loading={isSubmitting}
                showTrailingIcon={false}
                leadingIcon={
                  !isSubmitting ? <AppIcon name="check-double" /> : undefined
                }
              >
                {isSubmitting ? 'Aguarde...' : 'Aceitar convite'}
              </Button>
            </Modal.Footer>
          )}
        </form>
      </FormProvider>
    </Modal>
  )
}
